import {
  AccountSchema,
  CommunityPermissionFlag,
  CommunityPermissions,
  TeamMemberSchema,
} from "../data/types";

export const DEFAULT_COMMUNITY_PERMISSIONS: CommunityPermissions = {
  manageResources: false,
  manageRequests: false,
  manageMembers: false,
  manageCommunity: false,
  managePayments: false,
  viewMessages: false,
  accessSettings: false,
};

export const FULL_COMMUNITY_PERMISSIONS: CommunityPermissions = {
  manageResources: true,
  manageRequests: true,
  manageMembers: true,
  manageCommunity: true,
  managePayments: true,
  viewMessages: true,
  accessSettings: true,
};

type PermissionSource = Partial<CommunityPermissions> & {
  editKitchenListings?: boolean;
  accessBookings?: boolean;
};

/** Fill missing community permission flags, mapping retired kitchen-era keys. */
export const normalizeCommunityPermissions = (
  permissions?: PermissionSource | Record<string, never>
): CommunityPermissions => {
  const source = (permissions || {}) as PermissionSource;
  return {
    manageResources: Boolean(source.manageResources || source.editKitchenListings),
    manageRequests: Boolean(source.manageRequests || source.accessBookings),
    manageMembers: Boolean(source.manageMembers),
    manageCommunity: Boolean(source.manageCommunity),
    managePayments: Boolean(source.managePayments),
    viewMessages: Boolean(source.viewMessages),
    accessSettings: Boolean(source.accessSettings),
  };
};

const accountEmail = (account?: Pick<AccountSchema, "email" | "username"> | null): string =>
  String(account?.email || account?.username || "").trim().toLowerCase();

const memberEmail = (member?: TeamMemberSchema): string =>
  String(member?.email || member?.username || "").trim().toLowerCase();

/** Find the signed-in partner's team row by email or username. */
export const findTeamMemberByAccount = (
  team: TeamMemberSchema[] | undefined,
  account?: Pick<AccountSchema, "email" | "username"> | null
): TeamMemberSchema | undefined => {
  const email = accountEmail(account);
  if (!email || !team?.length) {
    return undefined;
  }
  return team.find((member) => memberEmail(member) === email);
};

/** Owners and unlisted community admins keep full dashboard access. */
export const hasUnrestrictedTeamAccess = (member?: TeamMemberSchema): boolean =>
  !member || member.role === "owner";

/** Resolve the signed-in partner's community permissions for routing and nav. */
export const getCurrentTeamPermissions = (
  team: TeamMemberSchema[] | undefined,
  account?: Pick<AccountSchema, "email" | "username"> | null
): CommunityPermissions => {
  const member = findTeamMemberByAccount(team, account);
  // Narrow first: unrestricted helper treats missing members as full access, so TS
  // cannot infer `member` is defined after a lone `hasUnrestrictedTeamAccess` check.
  if (!member || hasUnrestrictedTeamAccess(member)) {
    return FULL_COMMUNITY_PERMISSIONS;
  }
  return normalizeCommunityPermissions(member.permissions?.community?.permissions);
};

export const canAccess = (
  permissions: CommunityPermissions | undefined,
  flag: CommunityPermissionFlag
): boolean => Boolean(permissions?.[flag]);

export const canAccessAny = (
  permissions: CommunityPermissions | undefined,
  flags: CommunityPermissionFlag[]
): boolean => flags.some((flag) => canAccess(permissions, flag));

/** Sidebar item names that stay visible regardless of team permissions. */
export const UNGATED_SIDEBAR_ITEMS = new Set(["Collapse", "Dashboard", "Logout", "Leads CRM"]);

/** Permission flags required to show a sidebar item. */
export const sidebarItemPermissionFlags = (itemName: string): CommunityPermissionFlag[] | null => {
  switch (itemName) {
    case "Community":
      return ["manageCommunity"];
    case "Memberships":
      return ["manageMembers", "manageRequests"];
    case "Resources":
      return ["manageResources", "manageRequests"];
    case "Messages":
      return ["viewMessages"];
    case "Payments":
      return ["managePayments"];
    case "Settings":
      return ["accessSettings"];
    default:
      return null;
  }
};

/** Permission flags required for an authenticated dashboard path, or null if ungated. */
export const pathPermissionFlags = (pathname: string): CommunityPermissionFlag[] | null => {
  if (
    pathname === "/" ||
    pathname.startsWith("/leads") ||
    pathname.startsWith("/uat") ||
    pathname.startsWith("/verify-email") ||
    pathname.startsWith("/self-service")
  ) {
    return null;
  }
  if (pathname.startsWith("/community")) {
    return ["manageCommunity"];
  }
  if (pathname.startsWith("/memberships")) {
    return ["manageMembers", "manageRequests"];
  }
  if (pathname.startsWith("/resources") || pathname.startsWith("/inventory")) {
    return ["manageResources", "manageRequests"];
  }
  if (pathname.startsWith("/payments")) {
    return ["managePayments"];
  }
  if (pathname.startsWith("/messages")) {
    return ["viewMessages"];
  }
  if (pathname.startsWith("/settings")) {
    return ["accessSettings"];
  }
  return null;
};

export const TEAM_PERMISSION_LABELS: { id: CommunityPermissionFlag; label: string }[] = [
  { id: "manageResources", label: "Manage resources" },
  { id: "manageRequests", label: "Manage bookings and requests" },
  { id: "manageMembers", label: "Manage memberships" },
  { id: "manageCommunity", label: "Manage community" },
  { id: "managePayments", label: "Manage payments" },
  { id: "viewMessages", label: "View messages" },
  { id: "accessSettings", label: "Access settings" },
];
